import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import SEOFooter from '../../components/SEO/SEOFooter';
import {
  ArrowLeft, Brain, TrendingUp, Target, BarChart3,
  Zap, Award, ChevronDown, CheckCircle2, XCircle, Trophy
} from 'lucide-react';

const AISportsBettingPicks = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const sportsCoverage = [
    {
      sport: 'NFL',
      pickTypes: 'Spreads, Totals, Player Props, Parlays',
      accuracy: '68%',
      volume: '200+ picks/week',
      edge: 'Advanced pass rush metrics, weather impact'
    },
    {
      sport: 'NBA',
      pickTypes: 'Game Lines, Player Points, Rebounds, Assists',
      accuracy: '65%',
      volume: '300+ picks/week',
      edge: 'Rest days, back-to-back analysis'
    },
    {
      sport: 'MLB',
      pickTypes: 'Moneyline, Run Lines, Strikeout Props',
      accuracy: '63%',
      volume: '400+ picks/day',
      edge: 'Bullpen analysis, platoon splits'
    },
    {
      sport: 'Soccer',
      pickTypes: 'Match Results, Goals, Corners, Cards',
      accuracy: '67%',
      volume: '500+ picks/week',
      edge: 'Tactical formations, referee tendencies'
    }
  ];

  const aiFeatures = [
    {
      title: 'Real-Time Data Processing',
      description: 'Analyzes live stats, injuries, weather, and betting market movements within seconds',
      icon: <Zap className="w-6 h-6" />
    },
    {
      title: 'Professional-Grade Models',
      description: 'Built by ML engineers who are professional sports bettors with proven track records',
      icon: <Brain className="w-6 h-6" />
    },
    {
      title: 'Value Bet Detection',
      description: 'Identifies mispriced odds where bookmakers underestimate true probabilities',
      icon: <Target className="w-6 h-6" />
    },
    {
      title: 'Multi-Sport Expertise',
      description: 'Specialized models for NFL, NBA, MLB, Soccer, MMA, and more',
      icon: <Trophy className="w-6 h-6" />
    }
  ];

  const whyBetterThan = [
    {
      vs: 'Human Experts',
      advantage: 'Analyzes 1000x more data points in seconds',
      pickstarWin: true
    },
    {
      vs: 'ChatGPT',
      advantage: 'Specialized sports betting context, not general purpose',
      pickstarWin: true
    },
    {
      vs: 'Betting Tipsters',
      advantage: 'Transparent methodology, no hidden biases or cherry-picking',
      pickstarWin: true
    },
    {
      vs: 'Betting Forums',
      advantage: 'Data-driven predictions, not emotion or herd mentality',
      pickstarWin: true
    }
  ];

  const faqs = [
    {
      q: "What are AI sports betting picks?",
      a: "AI sports betting picks are predictions generated by artificial intelligence models that analyze massive amounts of sports data, statistics, trends, injuries, and betting markets to identify the most profitable betting opportunities."
    },
    {
      q: "How accurate are AI sports betting picks?",
      a: "PickStar AI maintains 63-68% accuracy across major sports (NFL, NBA, MLB, Soccer). While no system is perfect, this beats the 52.4% needed to break even against typical -110 odds, giving bettors a consistent edge."
    },
    {
      q: "Can I use AI picks for parlays?",
      a: "Yes! Our AI can generate correlated parlays and identify combinations with positive expected value. However, we recommend focusing on straight bets for consistent long-term profits."
    },
    {
      q: "How is PickStar AI different from ChatGPT?",
      a: "ChatGPT is a general-purpose AI. PickStar AI is built by ML engineers who are professional sports bettors, with specialized context engineering for sports betting. We process real-time betting data and use proprietary models trained on millions of sports bets."
    },
    {
      q: "Do AI picks work for live betting?",
      a: "Absolutely. Our AI excels at live betting by processing in-game stats and momentum shifts in real-time, often finding value that bookmakers miss during live action."
    }
  ];

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "AI Sports Betting Picks - Smarter Predictions with PickStar AI",
    "description": "Get professional AI sports betting picks for NFL, NBA, MLB, and Soccer. Data-driven predictions with 65%+ accuracy across all major sports.",
    "author": {
      "@type": "Organization",
      "name": "PickStar AI"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PickStar AI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pickstarai.com/logo.png"
      }
    },
    "datePublished": "2025-10-23",
    "dateModified": new Date().toISOString(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pickstarai.com/ai-sports-betting-picks"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="AI Sports Betting Picks | Free AI Predictions for NFL, NBA, MLB, Soccer"
        description="Get professional AI sports betting picks with 65%+ accuracy. Our machine learning models analyze NFL, NBA, MLB, Soccer for the smartest betting predictions. Free AI picks daily."
        keywords="ai sports betting picks, ai picks, ai betting predictions, sports predictions ai, ai betting tips, machine learning sports betting, nfl ai picks, nba ai picks, mlb ai picks, soccer ai predictions"
        canonical="https://pickstarai.com/ai-sports-betting-picks"
        ogImage="https://pickstarai.com/og-home-logo.png"
        structuredData={[structuredData, faqStructuredData]}
      />

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="border-b border-gray-200 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link
                to="/"
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
              <div className="text-xs text-gray-500 font-medium">
                UPDATED: {new Date().toLocaleDateString('en-US').toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
            <div className="text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <Brain className="w-10 h-10 md:w-12 md:h-12 text-blue-600" />
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 tracking-tight">
                  AI Sports Betting Picks
                </h1>
              </div>
              <p className="mt-4 text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
                Professional-grade sports predictions powered by machine learning.
                Get the edge with AI that processes millions of data points in seconds.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/chat')}
                  className="px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Get Free AI Picks
                </button>
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-8 py-3 bg-gray-100 text-gray-900 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  View Pricing
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="text-3xl font-bold text-gray-900">65%+</div>
              <div className="text-sm text-gray-500 mt-1">Win Rate</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="text-3xl font-bold text-gray-900">1M+</div>
              <div className="text-sm text-gray-500 mt-1">Bets Analyzed</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="text-3xl font-bold text-gray-900">4</div>
              <div className="text-sm text-gray-500 mt-1">Major Sports</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="text-3xl font-bold text-gray-900">24/7</div>
              <div className="text-sm text-gray-500 mt-1">Real-Time Analysis</div>
            </div>
          </div>

          {/* Sports Coverage */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              Sports We Cover
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {sportsCoverage.map((sport, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">{sport.sport}</h3>
                    <span className="text-2xl font-bold text-green-600">{sport.accuracy}</span>
                  </div>

                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-gray-500">Pick Types:</span>
                      <p className="text-gray-900 font-medium">{sport.pickTypes}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Volume:</span>
                      <p className="text-gray-900 font-medium">{sport.volume}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">AI Edge:</span>
                      <p className="text-gray-900 font-medium">{sport.edge}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* AI Features */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              What Makes Our AI Picks Different
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {aiFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-lg p-6"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 rounded-lg p-3 text-blue-600">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Why Better Than Alternatives */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              PickStar AI vs. The Competition
            </h2>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Comparison</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">PickStar AI Advantage</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Winner</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {whyBetterThan.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        vs. {item.vs}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {item.advantage}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {item.pickstarWin ? (
                          <CheckCircle2 className="w-6 h-6 text-green-600 mx-auto" />
                        ) : (
                          <XCircle className="w-6 h-6 text-red-600 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* How It Works */}
          <section className="mb-16 bg-gray-50 rounded-lg p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              How PickStar AI Works
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mb-4">
                  1
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Data Collection
                </h3>
                <p className="text-gray-600">
                  Our AI ingests real-time stats, injuries, weather, betting lines, and historical data from trusted sources.
                </p>
              </div>

              <div>
                <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mb-4">
                  2
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  AI Analysis
                </h3>
                <p className="text-gray-600">
                  Machine learning models process thousands of data points to identify patterns and mispriced odds.
                </p>
              </div>

              <div>
                <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mb-4">
                  3
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Smart Picks
                </h3>
                <p className="text-gray-600">
                  You receive confident predictions with reasoning, confidence levels, and optimal bet sizing recommendations.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              Frequently Asked Questions
            </h2>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
                  >
                    <span className="text-base font-medium text-gray-900">
                      {faq.q}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        expandedFaq === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {expandedFaq === index && (
                    <div className="px-6 pb-4 text-gray-600">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Bottom CTA */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 md:p-12 text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Start Winning with AI Picks Today
            </h2>
            <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
              Join thousands of smart bettors using PickStar AI to make data-driven decisions across all major sports.
            </p>
            <button
              onClick={() => navigate('/chat')}
              className="px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Get Free AI Picks Now
            </button>
          </div>
        </div>

        {/* SEO Footer */}
        <SEOFooter language="en" />
      </div>
    </>
  );
};

export default AISportsBettingPicks;
